/**
 * Supplier audits and the actions raised from them (construction Phase 2).
 * Backend: `audits` app — `/api/v1/audits/audits/`, `/api/v1/audits/actions/`.
 */
import api, { type AxiosResponse, type PaginatedResponse } from './client';

export type AuditType = 'site' | 'quality' | 'process' | 'hse' | 'compliance' | 'other';
export type AuditStatus = 'planned' | 'in_progress' | 'completed' | 'cancelled';
export type AuditOutcome = '' | 'pass' | 'conditional' | 'fail';
export type ActionStatus = 'open' | 'in_progress' | 'overdue' | 'completed' | 'cancelled';
export type ActionPriority = 'low' | 'medium' | 'high';

export interface AuditAction {
  id: number;
  audit: number | null;
  audit_reference: string | null;
  audit_title: string | null;
  customer: number;
  customer_name: string;
  supplier: number;
  supplier_name: string;
  title: string;
  description: string;
  owner: number | null;
  owner_name: string;
  due_date: string;
  priority: ActionPriority;
  priority_display: string;
  status: ActionStatus;
  status_display: string;
  days_overdue: number;
  completed_at: string | null;
  completion_notes: string;
  evidence: number | null;
  evidence_title: string | null;
  created_at: string;
  updated_at: string;
}

export interface SupplierAudit {
  id: number;
  reference: string;
  customer: number;
  customer_name: string;
  supplier: number;
  supplier_name: string;
  division: number | null;
  division_name: string | null;
  audit_type: AuditType;
  audit_type_display: string;
  title: string;
  scheduled_date: string;
  completed_date: string | null;
  auditor: number | null;
  auditor_name: string;
  status: AuditStatus;
  status_display: string;
  outcome: AuditOutcome;
  outcome_display: string;
  score: string | null;
  is_overdue: boolean;
  open_actions: number;
  total_actions: number;
  checklist: number | null;
  created_at: string;
  updated_at: string;
}

export interface AuditChecklistSummary {
  id: number;
  name: string;
  status: string;
  completion_percentage: number;
  score_percentage: number | null;
}

export interface SupplierAuditDetail extends SupplierAudit {
  summary: string;
  findings: string;
  actions: AuditAction[];
  checklist_summary: AuditChecklistSummary | null;
  created_by: number | null;
}

export interface AuditSummary {
  planned: number;
  in_progress: number;
  overdue: number;
  completed: number;
  open_actions: number;
  overdue_actions: number;
}

export interface AuditPerson {
  id: number;
  name: string;
}

export interface SupplierAuditPayload {
  supplier: number;
  division?: number | null;
  audit_type: AuditType;
  title: string;
  scheduled_date: string;
  completed_date?: string | null;
  auditor?: number | null;
  status?: AuditStatus;
  outcome?: AuditOutcome;
  score?: string | null;
  summary?: string;
  findings?: string;
  checklist?: number | null;
}

export interface AuditActionPayload {
  audit?: number | null;
  supplier?: number;
  title: string;
  description?: string;
  owner?: number | null;
  due_date: string;
  priority?: ActionPriority;
  status?: ActionStatus;
  completion_notes?: string;
  evidence?: number | null;
}

export type AuditListParams = Record<string, string | number | boolean>;

export const auditsAPI = {
  list: (params?: AuditListParams): Promise<AxiosResponse<PaginatedResponse<SupplierAudit>>> =>
    api.get('/audits/audits/', { params }),
  get: (id: number, params?: AuditListParams): Promise<AxiosResponse<SupplierAuditDetail>> =>
    api.get(`/audits/audits/${id}/`, { params }),
  create: (data: SupplierAuditPayload): Promise<AxiosResponse<SupplierAuditDetail>> =>
    api.post('/audits/audits/', data),
  update: (id: number, data: Partial<SupplierAuditPayload>): Promise<AxiosResponse<SupplierAuditDetail>> =>
    api.patch(`/audits/audits/${id}/`, data),
  remove: (id: number): Promise<AxiosResponse<void>> => api.delete(`/audits/audits/${id}/`),
  summary: (params?: AuditListParams): Promise<AxiosResponse<AuditSummary>> =>
    api.get('/audits/audits/summary/', { params }),
  people: (): Promise<AxiosResponse<AuditPerson[]>> => api.get('/audits/audits/people/'),

  listActions: (params?: AuditListParams): Promise<AxiosResponse<PaginatedResponse<AuditAction>>> =>
    api.get('/audits/actions/', { params }),
  createAction: (data: AuditActionPayload): Promise<AxiosResponse<AuditAction>> =>
    api.post('/audits/actions/', data),
  updateAction: (id: number, data: Partial<AuditActionPayload>): Promise<AxiosResponse<AuditAction>> =>
    api.patch(`/audits/actions/${id}/`, data),
  completeAction: (id: number, notes?: string): Promise<AxiosResponse<AuditAction>> =>
    api.post(`/audits/actions/${id}/complete/`, { notes: notes ?? '' }),
  removeAction: (id: number): Promise<AxiosResponse<void>> => api.delete(`/audits/actions/${id}/`),
};
